import { useEffect, useState } from "react";
import { inNativeShell } from "../lib/nativeShell";

/** True when App mounts the phone app (src/phone) instead of the desktop
 *  routes (2026-10-03).
 *
 *  A JS media-query gate rather than CSS breakpoints, on purpose: rendering
 *  both and hiding one still mounts both, so the desktop's state machine,
 *  toolbars and COM-backed fetches all ran on the phone. Mounting one tree
 *  means a phone downloads and runs only its own app.
 *
 *  Below 768 px (Tailwind's `md`, so a page's own `max-md:` rules never
 *  disagree): always the phone app.
 *
 *  Touch first up to 1280 px (2026-10-22, the Galaxy Z Fold): a Fold's inner
 *  screen is 750 to 830 px wide, which used to get the desktop app with its
 *  mouse-sized controls, and folding or unfolding swapped one app for the
 *  other, dropping whatever was open. A touch-only screen (no hover, a
 *  coarse pointer) and the Android app keep the phone app, which lays out
 *  side by side from 600 px (src/phone/ui/wide.ts). A laptop or a desktop,
 *  touchscreen or not (they hover), keeps the desktop app. */

const PHONE_QUERY = "(max-width: 767px), (hover: none) and (pointer: coarse) and (max-width: 1279px)";

function matches(): boolean {
  if (typeof window === "undefined") return false;
  if (inNativeShell()) return true;
  return window.matchMedia(PHONE_QUERY).matches;
}

export function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(matches);
  useEffect(() => {
    const mql = window.matchMedia(PHONE_QUERY);
    const onChange = () => setIsMobile(matches());
    mql.addEventListener("change", onChange);
    setIsMobile(matches());
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}
